/**
 * Create date-time formatter
 * @param options = {
            timeZoneName: 'short',
            weekday: undefined,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        };
 */
const dateFormatter = options => params => {
  if (!params) return "";

  return params.value !== undefined
    ? params.value
      ? new Date(params.value).toLocaleString("en-US", options)
      : ""
    : new Date(params).toLocaleString("en-US", options);
};

export const asInputDateString = (date: Date): string => {
  return date.toISOString().substring(0, 10);
};

export const asTimelessDate = params => {
  if (!params) return "";
  return params.value !== undefined
    ? params.value
      ? timelessUTCDate(params.value)
      : ""
    : timelessUTCDate(params);
};

const timelessUTCDate = params => {
  return (
    params.getUTCMonth() +
    1 +
    "/" +
    params.getUTCDate() +
    "/" +
    params.getUTCFullYear()
  );
};

export const asTimelessDateUTC = params => {
  if (!params) return "";
  return params.value !== undefined
    ? params.value
      ? timelessUTCDate(new Date(params.value))
      : ""
    : timelessUTCDate(new Date(params));
};

export const asDate = dateFormatter({
  year: "numeric",
  month: "numeric",
  day: "numeric"
});

export const asDateTime = dateFormatter({
  timeZoneName: "short",
  weekday: undefined,
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false
});

export const asDateTimeUTC = dateFormatter({
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZone: 'UTC',
  hour12: true
});

export const asFilenameDateTime = dateFormatter({
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: '2-digit',
  hour12: false
});

export const asTime = dateFormatter({
  timeZoneName: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23"
});

export function asCurrency(params, currency = 'USD', options = {}): string {
  currency = currency || 'USD';
  const x = extractParams(params);

  if (!isNaN(x) && x !== null && x !== undefined) {
    try {
      return x.toLocaleString("en-US", {
        style: "currency",
        currency,
        ...options
      });
    } catch {
      return `${currency} ${x}`;
    }
  }

  return "";
}

export function asShortCurrency(x: number, currency = 'USD'): string {

  return asCurrency(x, currency, { notation: 'compact' });
}

export function asPercent(value: number): string {
  return String(Math.round(value * 100)) + "%";
}

export function getPercentValue(part: number, full: number): number {
  return Math.round((part / full) * 100) || 0;
}

function extractParams(params: { value: any } | any) {
  return params == null || params.value === undefined ? params : params.value;
}

export function csvHyperlink(url) {
  return '=HYPERLINK(""' + url + '"")';
}

export const handleDateStringWithoutTimeZoneInfo = (dateString) => {
  if (typeof dateString !== "string") {
      return dateString;
    }
  
    const utcDateString = dateString + "Z";
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) {
      // Date string is not in a recognized format meaning it is either an invalid date or already has timezone information on it, return the string as-is
      return dateString;
    }
  
    // Date string is now a recognized UTC format, convert to UTC date string
    return date.toISOString();
}

export const handleDate = (date) => {
  return !!date ? new Date(asTimelessDateUTC(date)) : null;
}

export const getDateOnly = (date) => {
  if (!date) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
